import { useEffect, useMemo } from 'react';
import { useController } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { useAppDispatch, useAppSelector } from '@global/store/hooks';
import { countriesSelector } from '@global/store/slices/country/countrySelector';
import { getCountries } from '@global/store/slices/country/extraReducers/getCountries';
import { CountryService } from '@global/utils/CountryService';

import { Country } from '@global/types/types';
import { CommonSelect } from '@src/global/components/common/CommonSelect';
import { getAvailableCountries } from './helper';
import { SiteConfig } from '@src/global/utils/SiteConfig';

interface CountrySelectInputProps {
  selectedCountry?: { isoCodeTwo: string };
  disabled?: boolean;
  label?: string;
  fieldName: string;
  region?: string;
  restrictedCountries?: string[];
  enableLocaleFallback?: boolean;
}

const COUNTRY_VALUE_KEY = 'isoCodeTwo';
const COUNTRY_LABEL_KEY = 'name';
const COUNTRY_ID_KEY = 'id';
const COUNTRY_SELECT_ID = 'card_billingaddress_country';

export const CountrySelectInput = ({
  selectedCountry,
  disabled,
  label,
  fieldName,
  region,
  restrictedCountries,
  enableLocaleFallback = true,
}: CountrySelectInputProps) => {
  const { t } = useTranslation();
  const countries = useAppSelector(countriesSelector);
  const dispatch = useAppDispatch();

  useEffect(() => {
    dispatch(getCountries());
  }, []);

  const handelSelect = (value) => {
    field.onChange(value);
    stateField.value = ''; // reset state when country is changed
  };

  const availableCountries = useMemo(
    () => getAvailableCountries(countries, region, restrictedCountries),
    [countries, region, restrictedCountries]
  );

  const { iso } = SiteConfig.getConfig();
  const defaultCountryIsoCode = useMemo(() => {
    if (selectedCountry?.isoCodeTwo) {
      return selectedCountry.isoCodeTwo;
    }

    if (!enableLocaleFallback) {
      return '';
    }

    return (
      CountryService.getCountryByIsoCode(availableCountries, iso)?.isoCodeTwo ||
      ''
    );
  }, [availableCountries, enableLocaleFallback, iso, selectedCountry]);

  const { field, fieldState } = useController({
    name: fieldName,
    defaultValue: defaultCountryIsoCode,
  });
  const { field: stateField } = useController({ name: 'state' });

  const listOfCountries = useMemo<Country[]>(
    () =>
      availableCountries.reduce((acc, country) => {
        if (country.isoCodeTwo === field.value) {
          return [country, ...acc];
        }
        return [...acc, country];
      }, []),
    [field.value, availableCountries]
  );

  useEffect(() => {
    if (defaultCountryIsoCode && defaultCountryIsoCode !== field.value) {
      handelSelect(defaultCountryIsoCode);
    }
  }, [defaultCountryIsoCode]);

  return (
    <CommonSelect
      label={label ?? t('activeForm.countryLabel')}
      id={COUNTRY_SELECT_ID}
      placeholder={t('activeForm.countrySelect')}
      options={listOfCountries}
      onChange={handelSelect}
      itemValue={COUNTRY_VALUE_KEY}
      itemLabel={COUNTRY_LABEL_KEY}
      itemId={COUNTRY_ID_KEY}
      disabled={disabled}
      value={field.value || ''}
      isValid={field.value && !fieldState.error}
      error={!!fieldState.error}
      errorMessage={fieldState.error?.message}
    />
  );
};
